import { createSlice } from "@reduxjs/toolkit";
import type { RootState } from "store";

interface IAuthStateProps {
  token: string;
  publicToken: string;
  /**
   * The Order.id the current `publicToken` was minted against (CLE-2181).
   *
   * Patient tokens are order-bound, so a token is only usable for the order it was issued for.
   * `publicToken` is a single global slot reused across every order a user visits, so without
   * this the SPA cannot tell whether the token in hand matches the order it is about to call —
   * and a request fired during the window before a fresh mint lands is sent with the PREVIOUS
   * order's token and rejected with PATIENT_TOKEN_ORDER_MISMATCH.
   *
   * That used to be invisible: the pre-CLE-2181 API accepted any validly-signed token for any
   * order, so the stale token "worked". Consumers must now gate on `isPatientTokenReadyFor`.
   */
  publicTokenOrderId: string | null;
}

const initialState: IAuthStateProps = {
  token: "",
  publicToken: "",
  publicTokenOrderId: null,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    onTokenChange: (state, action: { payload: { token: string } }) => {
      state.token = action.payload.token;
    },
    onPublicTokenChange: (
      state,
      action: { payload: { token: string; orderId?: string | null } }
    ) => {
      state.publicToken = action.payload.token;
      state.publicTokenOrderId = action.payload.token
        ? action.payload.orderId ?? null
        : null;
    },
  },
});

export const { onTokenChange, onPublicTokenChange } = authSlice.actions;
export const authSelector = (state: RootState) => state.authSlice;

/** True when the token in redux was minted for `orderId` and is therefore usable against it. */
export const isPatientTokenReadyFor = (
  state: RootState,
  orderId?: string | null
): boolean =>
  !!orderId &&
  !!state.authSlice.publicToken &&
  state.authSlice.publicTokenOrderId === orderId;

export default authSlice.reducer;
